import { Auth } from '@aws-amplify/auth';
import retry from 'async-retry';
import { Gunzip } from 'zlibt2';

import { retries } from '@/constants/env';
import { SessionError } from '@/errors/sessionError';
import { api } from '@/share';

/**
 * メソッド
 */
export type MethodType = 'get' | 'post' | 'put' | 'delete';

/**
 * WagakoApiBase
 */
export abstract class WagakoApiBase {
  /** API URL */
  protected abstract get apiUrl(): string;

  /**
   * S3パスを切る
   * @param path パス
   * @returns 結果
   */
  protected splitS3Path(path: string) {
    const tmp = path.split('/');
    return {
      /** Entity名 */
      entityName: encodeURIComponent(tmp[0]),
      /** Entityキー */
      entityKey: encodeURIComponent(tmp[1]),
      /** 名前 */
      name: encodeURIComponent(tmp[2]),
    };
  }

  /**
   * Jwtトークンを取得する
   * @returns トークン
   */
  protected async getIdJwtToken() {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (e) {
      let msg = 'SessionError in getIdJwtToken (wagakoApiBase)';
      if (e instanceof Error) {
        msg += `:${e.message}`;
      }
      throw new SessionError(msg);
    }
  }

  /**
   * APIを呼ぶ
   * @param input 入力
   * @returns 結果
   */
  protected async callApi<S extends {
    /** パス */
    path: string,
    /** パスパラメタ */
    pathParameters?: Record<string, string> | undefined,
    /** メソッド */
    method: MethodType,
    /** ボディ */
    body?: any | undefined,
  }, T extends {
    /** メッセージ */
    message: 'OK',
    /** データ */
    data: any,
    /** コード */
    code: undefined,
    /** ステータスコード */
    statusCode: number,
  } | {
    /** メッセージ */
    message: string,
    /** データ */
    data: undefined,
    /** コード */
    code: string,
    /** ステータスコード */
    statusCode: number,
  }>(
    input: S,
  ): Promise<T> {
    const {
      path,
      pathParameters,
      method,
      body,
    } = input;
    let url = `${this.apiUrl}${path}`;
    if (pathParameters) {
      const keys = Object.keys(pathParameters);
      for (let i = 0; i < keys.length; i += 1) {
        url = url.replace(`{${keys[i]}}`, pathParameters[keys[i]]);
      }
    }
    return retry(async () => {
      const token = await this.getIdJwtToken();
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: token,
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: body ? JSON.stringify(body) : null,
      });
      // 204の場合、bodyが空になるためres.json()でエラーになるのでここで対応
      if (res.status === 204) {
        return { message: 'NoContent', statusCode: 204 } as T;
      }
      const json = await res.json() as T;
      return json;
    }, {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry fetch ${path}: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * 画像API
   * @param input 入力
   * @param filename ファイル名
   * @returns 結果
   */
  protected async callImageApi<S extends {
    /** パス */
    path: string,
    /** パスパラメタ */
    pathParameters?: Record<string, string> | undefined,
    /** メソッド */
    method: MethodType,
  }>(
    input: S,
    filename: string,
  ): Promise<{
    /** メッセージ */
    message: string,
    /** Blob */
    blob: Blob | undefined,
  }> {
    const {
      path,
      pathParameters,
      method,
    } = input;
    let url = `${this.apiUrl}${path}`;
    if (pathParameters) {
      const keys = Object.keys(pathParameters);
      for (let i = 0; i < keys.length; i += 1) {
        url = url.replace(`{${keys[i]}}`, pathParameters[keys[i]]);
      }
    }
    const mime = this.getImageContentTypeFromFilename(filename);
    if (mime) {
      return retry(async () => {
        const token = await this.getIdJwtToken();
        const res = await fetch(url, {
          method,
          headers: {
            Authorization: token,
            Accept: mime,
            'Content-Type': 'application/json; charset=utf-8',
          },
        });
        // 204の場合、bodyが空になるためres.json()でエラーになるのでここで対応
        if (res.status === 204) {
          return { message: 'NoContent', blob: undefined };
        }
        const blob = await res.blob();
        return { message: 'OK', blob };
      }, {
        retries,
        onRetry: (err, count) => {
          api.logger.warn(`Retry fetch ${path}: ${count}:${err.name}:${err.message}`);
        },
      });
    }
    return { message: '画像でないファイル拡張子', blob: undefined };
  }

  /**
   * ファイルをS3に保存する.
   * @param url URL
   * @param body ボディ
   * @returns 結果
   */
  protected async apiSaveFileToS3(url: string, body: Blob): Promise<{
    /** ステータスコード */
    status: number;
    /** メッセージ */
    msg: string;
  }> {
    return retry(async () => {
      const result = await fetch(url, {
        method: 'put',
        body,
      });
      if (!result.ok && ![403].includes(result.status)) {
        const errorText = await result.text();
        throw new Error(`Error in apiSaveFileToS3: ${result.status} - ${errorText}`);
      }
      return {
        status: result.status,
        msg: '',
      };
    }, {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry fetch apiSaveFileToS3: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * ファイルをS3に保存する(POST版).
   * PreSignedPostを使うことで制限をかけられる.
   * @param url URL
   * @param fields Fields
   * @param body ボディ
   * @returns 結果
   */
  protected async apiPostFileToS3(
    url: string,
    fields: { [key: string]: string },
    body: Blob,
  ) {
    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append('file', body);

    const result = await fetch(url, {
      method: 'post',
      body: formData,
    })
      .then((res: Response) => ({
        /** HTTPステータス */
        status: res.status,
        /** メッセージ */
        msg: '',
      }))
      .catch((error) => ({
        /** HTTPステータス */
        status: 999,
        /** メッセージ */
        msg: `${error}`,
      }));
    return result;
  }

  /**
   * apiGetFileFromS3
   * @param url URL
   * @returns 結果
   */
  protected async apiGetFileFromS3(url: string): Promise<{
    /** ステータス */
    status: number;
    /** コンテンツ */
    content: ArrayBuffer;
  } | {
    /** ステータス */
    status: number;
    /** コンテンツ */
    content: any;
  }> {
    return retry(async () => {
      const result = await fetch(url, {
        method: 'get',
      });
      if (!result.ok && ![403, 404].includes(result.status)) {
        const errorText = await result.text();
        throw new Error(`Error in apiGetFileFromS3: ${result.status} - ${errorText}`);
      }
      const content = await result.arrayBuffer();
      return {
        status: result.status,
        content,
      };
    }, {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry fetch apiGetFileFromS3: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * gunzipText
   * @param data データ
   * @returns テキスト
   */
  gunzipText(data: ArrayBuffer) {
    const gunzip = new Gunzip(new Uint8Array(data));
    const c = gunzip.decompress() as Uint8Array;
    return new TextDecoder().decode(c);
  }

  private getImageContentTypeFromFilename(filename: string) {
    const tmp = filename.split('.');
    const ext = tmp[tmp.length - 1].toLowerCase();
    if (['webp'].includes(ext)) {
      return 'image/webp';
    }
    if (['jpg', 'jpeg', 'jpe', 'jfif', 'pjpeg', 'pjp'].includes(ext)) {
      return 'image/jpeg';
    }
    if (['png'].includes(ext)) {
      return 'image/png';
    }
    if (['gif'].includes(ext)) {
      return 'image/gif';
    }
    if (['bmp', 'dib'].includes(ext)) {
      return 'image/bmp';
    }
    if (['svg', 'svgz'].includes(ext)) {
      return 'image/svg+xml';
    }
    // eslint-disable-next-line no-console
    console.error(`不明な画像拡張子:${ext}`);
    return undefined;
  }
}
